import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";

const POLL_MS = 3000;

/**
 * Modal para enviar Estado de Cuenta por correo a filiales seleccionadas.
 *
 * Props:
 *   selectedIds  — array de { filialId, filialLabel, propietario }
 *   fechaInicio  — string YYYY-MM-DD (default actual del dashboard)
 *   fechaFinal   — string YYYY-MM-DD
 *   onClose()    — cerrar modal
 *   onDone()     — callback al terminar envío
 */
export default function EmailEstadoCuentaModal({
  selectedIds = [],
  fechaInicio: propFI,
  fechaFinal:  propFF,
  onClose,
  onDone,
}) {
  const { t } = useTranslation();

  const today  = new Date();
  const defFI  = propFI || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  const defFF  = propFF || (() => {
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
  })();

  const [fechaInicio, setFechaInicio] = useState(defFI);
  const [fechaFinal,  setFechaFinal]  = useState(defFF);
  const [moneda,      setMoneda]      = useState("CRC");
  const [idioma,      setIdioma]      = useState("es");
  const [loading,     setLoading]     = useState(false);
  const [taskId,      setTaskId]      = useState(null);
  const [result,      setResult]      = useState(null);
  const [error,       setError]       = useState("");
  const pollRef = useRef(null);

  // Polling
  useEffect(() => {
    if (!taskId) return;
    pollRef.current = setInterval(async () => {
      try {
        const res  = await apiFetch(`/api/finance/cuentaXCobrar/email-status/?task_id=${taskId}`);
        const data = await res.json();
        if (data.done) {
          clearInterval(pollRef.current);
          setLoading(false);
          setTaskId(null);
          setResult(data);
          onDone?.();
        }
      } catch { /* ignore */ }
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [taskId, onDone]);

  async function handleSend() {
    if (!fechaInicio || !fechaFinal) {
      setError("Seleccione fechas de inicio y fin.");
      return;
    }
    if (selectedIds.length === 0) {
      setError("No hay filiales seleccionadas.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res  = await apiFetch("/api/finance/cuentaXCobrar/enviar-estado-email/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filial_ids:   selectedIds.map((x) => x.filialId),
          fecha_inicio: fechaInicio,
          fecha_final:  fechaFinal,
          moneda,
          idioma,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Error al iniciar el envío.");
        setLoading(false);
        return;
      }
      setTaskId(data.task_id);
    } catch (e) {
      setError("Error de red: " + e.message);
      setLoading(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "8px 10px",
    background: "var(--bg-input)", border: "1px solid var(--border-card)",
    borderRadius: 6, color: "var(--text-primary)", fontSize: "0.85rem",
    colorScheme: "light dark", outline: "none",
    boxSizing: "border-box", transition: "border-color 0.15s",
  };
  const sectionLabel = {
    fontSize: "0.67rem", fontWeight: 700, letterSpacing: "1.1px",
    textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8,
  };
  const SegBtn = ({ active, onClick, children, accentColor = "#f97316" }) => (
    <button onClick={onClick} style={{
      flex: 1, padding: "10px 0", border: "none",
      background: active ? `${accentColor}22` : "transparent",
      color: active ? accentColor : "var(--text-muted)",
      fontWeight: active ? 700 : 400, fontSize: "0.88rem",
      cursor: "pointer", transition: "all 0.15s",
    }}>
      {children}
    </button>
  );

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(5px)", zIndex: 1000,
      }} />

      <div onClick={(e) => e.stopPropagation()} style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 16, width: "min(520px,95vw)", zIndex: 1001,
        boxShadow: "0 32px 80px rgba(0,0,0,0.65)", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg,rgba(96,165,250,0.13) 0%,rgba(96,165,250,0.04) 100%)",
          borderBottom: "1px solid var(--border-card)", padding: "18px 22px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 11,
              background: "rgba(96,165,250,0.14)", border: "1px solid rgba(96,165,250,0.28)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fa-solid fa-envelope" style={{ color: "#60a5fa", fontSize: "1.1rem" }} />
            </div>
            <div>
              <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.2 }}>
                Enviar Estado de Cuenta
              </div>
              <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 2 }}>
                {selectedIds.length} filial{selectedIds.length !== 1 ? "es" : ""} seleccionada{selectedIds.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", fontSize: "1.1rem", padding: "4px 6px",
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Filiales badge list */}
          <div>
            <div style={sectionLabel}>Filiales seleccionadas</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 80, overflowY: "auto" }}>
              {selectedIds.map((s) => (
                <span key={s.filialId} style={{
                  padding: "3px 10px", borderRadius: 12, fontSize: "0.76rem",
                  background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.25)",
                  color: "#60a5fa", fontWeight: 600,
                }}>
                  {s.filialLabel}
                </span>
              ))}
            </div>
          </div>

          {/* Fechas */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={sectionLabel}>Fecha inicio</div>
              <input type="date" value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#60a5fa")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--border-card)")}
              />
            </div>
            <div>
              <div style={sectionLabel}>Fecha final</div>
              <input type="date" value={fechaFinal}
                onChange={(e) => setFechaFinal(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#60a5fa")}
                onBlur={(e)  => (e.target.style.borderColor = "var(--border-card)")}
              />
            </div>
          </div>

          <div style={{ borderTop: "1px solid var(--border-card)" }} />

          {/* Moneda */}
          <div>
            <div style={sectionLabel}>Moneda del reporte</div>
            <div style={{
              display: "flex", borderRadius: 9,
              border: "1px solid var(--border-card)", overflow: "hidden",
            }}>
              <SegBtn active={moneda === "CRC"} onClick={() => setMoneda("CRC")}>₡ Colones</SegBtn>
              <div style={{ width: 1, background: "var(--border-card)" }} />
              <SegBtn active={moneda === "USD"} onClick={() => setMoneda("USD")}>$ Dólares</SegBtn>
            </div>
          </div>

          {/* Idioma */}
          <div>
            <div style={sectionLabel}>Idioma del correo</div>
            <div style={{
              display: "flex", borderRadius: 9,
              border: "1px solid var(--border-card)", overflow: "hidden",
            }}>
              <SegBtn active={idioma === "es"} onClick={() => setIdioma("es")} accentColor="#818cf8">
                🇨🇷 Español
              </SegBtn>
              <div style={{ width: 1, background: "var(--border-card)" }} />
              <SegBtn active={idioma === "en"} onClick={() => setIdioma("en")} accentColor="#818cf8">
                🇺🇸 English
              </SegBtn>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)",
              borderLeft: "3px solid #f87171", borderRadius: 6, padding: "8px 12px",
              color: "#fca5a5", fontSize: "0.82rem",
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }} />
              {error}
            </div>
          )}

          {/* Resultado */}
          {result && (
            <div style={{
              background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.25)",
              borderRadius: 8, padding: "12px 16px",
            }}>
              <p style={{ margin: "0 0 6px", fontWeight: 700, color: "#4ade80", fontSize: "0.87rem" }}>
                <i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />
                Envío completado
              </p>
              <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                Enviados: <strong style={{ color: "#4ade80" }}>{result.total_enviados}</strong>
                {result.total_errores > 0 && (
                  <> · Errores: <strong style={{ color: "#f87171" }}>{result.total_errores}</strong></>
                )}
              </p>
              {result.errores?.length > 0 && (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: "0.76rem", color: "#f87171" }}>
                  {result.errores.map((e, i) => (
                    <li key={i}>{e.filial || e.factura}: {e.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Progreso */}
          {loading && taskId && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.2)",
              borderRadius: 8, padding: "12px 16px",
            }}>
              <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#60a5fa" }} />
              <span style={{ fontSize: "0.83rem", color: "var(--text-secondary)" }}>
                Enviando correos, por favor espere…
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: "1px solid var(--border-card)", background: "var(--hover-bg)",
          padding: "14px 24px", display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button onClick={onClose} disabled={loading && !!taskId} style={{
            padding: "9px 18px", background: "transparent",
            border: "1px solid var(--border-card)", borderRadius: 8,
            color: "var(--text-muted)", fontSize: "0.85rem", cursor: "pointer",
          }}>
            {result ? "Cerrar" : "Cancelar"}
          </button>
          {!result && (
            <button onClick={handleSend} disabled={loading || selectedIds.length === 0}
              style={{
                padding: "9px 22px",
                background: loading ? "rgba(96,165,250,0.6)" : "#60a5fa",
                color: "#fff", border: "none", borderRadius: 8,
                fontWeight: 700, fontSize: "0.85rem",
                cursor: loading || selectedIds.length === 0 ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: loading ? "none" : "0 4px 16px rgba(96,165,250,0.38)",
                opacity: selectedIds.length === 0 ? 0.5 : 1,
              }}
              onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = "#3b82f6"; }}
              onMouseOut={(e)  => { if (!loading) e.currentTarget.style.background = "#60a5fa"; }}
            >
              {loading ? (
                <><i className="fa-solid fa-circle-notch fa-spin" /> Enviando…</>
              ) : (
                <><i className="fa-solid fa-paper-plane" /> Enviar Estado de Cuenta</>
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
